import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { signIn } from "@/utils/auth/client";
import { LoaderCircle } from "lucide-react";
import { UseTheme } from "@/components/ThemeProvider";
import { HTMLAttributes, useEffect, useRef, useState } from "react";
import { useEnabledAuthProviderPublicQuery } from "@/api/queries/admin/AuthProviderQueries";

const OAuthForm = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [providerInProgress, setProviderInProgress] = useState<"microsoft" | "google" | null>(null);
    const [error, setError] = useState<string | null>(null);
    const { theme, setTheme } = UseTheme();
    const originalTheme = useRef(theme);
    
    // Fetch enabled auth providers using public endpoint (no auth required)
    const { data: enabledProviders, isLoading: isLoadingProviders } = useEnabledAuthProviderPublicQuery();

    useEffect(() => {
            // Store the current theme and switch to light for this page
            if (theme !== "light") {
                setTheme("light");
            }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Debug log to see what providers we get
    useEffect(() => {
        if (enabledProviders) {
            console.log("Enabled providers from API:", enabledProviders);
        }
    }, [enabledProviders]);

    const handleMicrosoftSignIn = async () => {
        console.log("Microsoft sign-in button clicked!");
        setError(null);
        setProviderInProgress("microsoft");
        setIsLoading(true);

        try {
            // Call social sign-in directly - it should redirect to Microsoft
            const result = await signIn.social({
                provider: "microsoft",
                callbackURL: window.location.origin
            });
            // If we get here without redirect, there might be an issue
            if (result && result.error) {
                throw new Error(result.error.message || "Microsoft sign-in failed");
            }
            setTheme(originalTheme.current);
        } catch (error: any) {
            console.error("Microsoft sign-in error:", error);
            console.error("Error details:", {
                message: error.message,
                stack: error.stack,
                name: error.name,
            });
            setError(error.message || "Microsoft sign-in failed");
        } finally {
            setIsLoading(false);
            setProviderInProgress(null);
        }
    };

    const handleGoogleSignIn = async () => {
        setError(null);
        setProviderInProgress("google");
        setIsLoading(true);

        try {
            const result = await signIn.social({
                provider: "google",
                callbackURL: window.location.origin + "/",
            });

            if (result && result.error) {
                throw new Error(result.error.message || "Google sign-in failed");
            }
        } catch (error: any) {
            console.error("Google sign-in error:", error);
            setError(error.message || "Google sign-in failed");
        } finally {
            setIsLoading(false);
            setProviderInProgress(null);
        }
    };

    // Provider configuration for rendering
    const providerConfig = {
        microsoft: {
            name: "Microsoft",
            icon: "/assets/providers/microsoft.svg",
            handler: handleMicrosoftSignIn,
        },
        google: {
            name: "Google",
            icon: "/assets/providers/google.svg",
            handler: handleGoogleSignIn,
        },
    };

    // If loading providers, show loading state
    if (isLoadingProviders) {
        return null;
    }

    // If no enabled providers, return null (render nothing)
    if (!enabledProviders || enabledProviders.length === 0) {
        return null;
    }

    return (
        <div className={cn("grid gap-6", className)} {...props}>
            <div className="grid gap-2">
                {error && <div className="mb-2 text-center text-sm text-red-600">{error}</div>}
                {enabledProviders.map((provider) => {
                    const config = providerConfig[provider.provider as keyof typeof providerConfig];
                    if (!config) return null;

                    return (
                        <Button
                            key={provider.id}
                            variant="outline"
                            type="button"
                            disabled={isLoading}
                            onClick={config.handler}
                        >
                            {isLoading && providerInProgress === provider.provider ? (
                                <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <img src={config.icon} className="mr-2 h-4 w-4" alt={`${config.name} logo`} />
                            )}{" "}
                            Sign In with {config.name}
                        </Button>
                    );
                })}
            </div>
        </div>
    );
};

export default OAuthForm;
